/**
 * UserStatusBadge Component
 * Visual indicators for user status
 */

import React from 'react';
import { Chip } from '@mui/material';
import {
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Shield as AdminIcon,
  Email as EmailIcon,
  EmailOutlined as UnverifiedIcon,
} from '@mui/icons-material';

interface UserStatusBadgeProps {
  type: 'active' | 'admin' | 'verified';
  value: boolean;
  size?: 'small' | 'medium';
}

const UserStatusBadge: React.FC<UserStatusBadgeProps> = ({ type, value, size = 'small' }) => {
  const configs = {
    active: {
      trueLabel: 'Active',
      falseLabel: 'Inactive',
      trueColor: 'success' as const,
      falseColor: 'default' as const,
      trueIcon: <ActiveIcon />,
      falseIcon: <InactiveIcon />,
    },
    admin: {
      trueLabel: 'Admin',
      falseLabel: 'User',
      trueColor: 'primary' as const,
      falseColor: 'default' as const,
      trueIcon: <AdminIcon />,
      falseIcon: null,
    },
    verified: {
      trueLabel: 'Verified',
      falseLabel: 'Unverified',
      trueColor: 'success' as const,
      falseColor: 'warning' as const,
      trueIcon: <EmailIcon />,
      falseIcon: <UnverifiedIcon />,
    },
  };

  const config = configs[type];
  const isTrue = value;

  if (type === 'admin' && !isTrue) {
    return null; // Don't show badge for regular users
  }

  const icon = isTrue ? config.trueIcon : config.falseIcon;
  
  return (
    <Chip
      label={isTrue ? config.trueLabel : config.falseLabel}
      color={isTrue ? config.trueColor : config.falseColor}
      {...(icon && { icon })}
      size={size}
      variant={isTrue ? 'filled' : 'outlined'}
      sx={{ fontWeight: 500 }}
    />
  );
};

export default UserStatusBadge;